import dayjs from 'dayjs';

import { ShipmentResponse, ShipmentTrackingEventResponse } from '@/api/data-contracts';

import { SHIPMENT_STATUS_LABELS, ShipmentStatusValue } from './shipmentStatus';

export type AwbListTab = 'active' | 'history';

export const ACTIVE_AWB_STATUSES: ShipmentStatusValue[] = [
  'PENDING',
  'REGISTERED',
  'PICKED_UP',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'PROBLEM',
  'UNKNOWN',
];

export const HISTORY_AWB_STATUSES: ShipmentStatusValue[] = [
  'DELIVERED',
  'CANCELLED',
  'RETURNED',
  'COMPENSATED',
];

export const AWB_TAB_STATUSES: Record<AwbListTab, ShipmentStatusValue[]> = {
  active: ACTIVE_AWB_STATUSES,
  history: HISTORY_AWB_STATUSES,
};

export const getShipmentLastUpdate = (shipment: ShipmentResponse): string =>
  [
    shipment.createdAt,
    shipment.generatedAt,
    shipment.outForDeliveryAt,
    shipment.deliveredAt,
    shipment.pollingStoppedAt,
  ]
    .filter((value): value is string => !!value)
    .reduce((latest, value) => (dayjs(value).isAfter(latest) ? value : latest));

export const selectAwbTabRows = (
  shipments: ShipmentResponse[],
  tab: AwbListTab,
): ShipmentResponse[] =>
  shipments
    .filter((shipment) => AWB_TAB_STATUSES[tab].includes(shipment.status))
    .map((shipment) => ({ shipment, lastUpdate: dayjs(getShipmentLastUpdate(shipment)).valueOf() }))
    .sort((a, b) => b.lastUpdate - a.lastUpdate)
    .map(({ shipment }) => shipment);

export type AwbListStatusKey =
  | 'pickup_waiting'
  | 'in_delivery'
  | 'delivered'
  | 'cancelled'
  | 'verifying'
  | ShipmentStatusValue;

export type AwbStatusTone =
  | 'pickup'
  | 'transit'
  | 'delivered'
  | 'cancelled'
  | 'pending'
  | 'exception';

export interface AwbListStatus {
  key: AwbListStatusKey;
  labelKey: string;
  tone: AwbStatusTone;
}

const AWB_LIST_STATUSES: Partial<Record<ShipmentStatusValue, AwbListStatus>> = {
  REGISTERED: { key: 'pickup_waiting', labelKey: 'awb.status_pickup_waiting', tone: 'pickup' },
  PICKED_UP: { key: 'in_delivery', labelKey: 'awb.status_in_delivery', tone: 'transit' },
  IN_TRANSIT: { key: 'in_delivery', labelKey: 'awb.status_in_delivery', tone: 'transit' },
  OUT_FOR_DELIVERY: { key: 'in_delivery', labelKey: 'awb.status_in_delivery', tone: 'transit' },
  DELIVERED: { key: 'delivered', labelKey: 'awb.status_delivered', tone: 'delivered' },
  CANCELLED: { key: 'cancelled', labelKey: 'awb.status_cancelled', tone: 'cancelled' },
  PENDING: { key: 'verifying', labelKey: 'awb.status_verifying', tone: 'pending' },
};

export const getAwbListStatus = (shipment: { status: ShipmentStatusValue }): AwbListStatus =>
  AWB_LIST_STATUSES[shipment.status] ?? {
    key: shipment.status,
    labelKey: SHIPMENT_STATUS_LABELS[shipment.status],
    tone: 'exception',
  };

export interface AwbListFilters {
  search: string;
  clientIds: string[];
  statusKeys: string[];
  from?: string;
  to?: string;
}

export const getAwbCreatedAt = (shipment: ShipmentResponse): string =>
  shipment.generatedAt ?? shipment.createdAt;

export const filterAwbRows = (
  shipments: ShipmentResponse[],
  { search, clientIds, statusKeys, from, to }: AwbListFilters,
): ShipmentResponse[] => {
  const term = search.trim().toLowerCase();
  return shipments.filter((shipment) => {
    if (clientIds.length > 0 && !clientIds.includes(shipment.clientId)) return false;
    if (statusKeys.length > 0 && !statusKeys.includes(getAwbListStatus(shipment).key)) {
      return false;
    }
    const createdDay = dayjs(getAwbCreatedAt(shipment)).format('YYYY-MM-DD');
    if (from && createdDay < from) return false;
    if (to && createdDay > to) return false;
    if (!term) return true;
    return [shipment.awbNumber, shipment.recipient?.name].some((value) =>
      value?.toLowerCase().includes(term),
    );
  });
};

export const getAwbStatusOptions = (
  shipments: ShipmentResponse[],
): Pick<AwbListStatus, 'key' | 'labelKey'>[] => {
  const seen = new Map<string, string>();
  shipments.forEach((shipment) => {
    const status = getAwbListStatus(shipment);
    if (!seen.has(status.key)) seen.set(status.key, status.labelKey);
  });
  return Array.from(seen, ([key, labelKey]) => ({ key: key as AwbListStatusKey, labelKey }));
};

export type TimelineStepState = 'done' | 'current' | 'todo' | 'cancelled';

export interface ShipmentTimelineStep {
  key: 'created' | 'pickedUp' | 'delivered' | 'cancelled';
  state: TimelineStepState;
  at?: string;
}

const COLLECTED: ShipmentStatusValue[] = [
  'PICKED_UP',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'PROBLEM',
  'UNKNOWN',
  'DELIVERED',
  'RETURNED',
  'COMPENSATED',
];

const firstEventAt = (
  events: ShipmentTrackingEventResponse[],
  statuses: ShipmentStatusValue[],
): string | undefined =>
  events
    .filter((event) => statuses.includes(event.status))
    .map((event) => event.at)
    .sort()[0];

export const buildShipmentTimeline = (
  shipment: ShipmentResponse,
  events: ShipmentTrackingEventResponse[],
): ShipmentTimelineStep[] => {
  const created: ShipmentTimelineStep = {
    key: 'created',
    state: 'done',
    at: shipment.generatedAt ?? shipment.createdAt,
  };
  const collected = COLLECTED.includes(shipment.status);
  const delivered = shipment.status === 'DELIVERED';

  const pickedUp: ShipmentTimelineStep = {
    key: 'pickedUp',
    state: collected ? (delivered ? 'done' : 'current') : 'todo',
    at: collected ? firstEventAt(events, COLLECTED) : undefined,
  };

  if (shipment.status === 'CANCELLED') {
    return [
      created,
      { ...pickedUp, state: 'todo' },
      { key: 'cancelled', state: 'cancelled', at: shipment.pollingStoppedAt },
    ];
  }

  return [
    created,
    pickedUp,
    {
      key: 'delivered',
      state: delivered ? 'done' : 'todo',
      at: delivered ? shipment.deliveredAt : undefined,
    },
  ];
};
